import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ApiService } from '@api/api.service';
import { RavIconCircled } from '@shared/icon/icon';
import { ModalService } from '@shared/modal/modal.service';
import { FormatService } from '@shared/services/format.service';
import { Subscription } from 'rxjs';
import { EmailValidator } from 'src/app/validator/email.validator';
import { FormValidator } from 'src/app/validator/form.validator';
import { EchoService } from '../../shared/services/echo.service';

@Component({
  selector: 'rav-share-document',
  templateUrl: './share-document.component.html',
  styleUrls: ['./share-document.component.scss']
})
export class ShareDocumentComponent implements OnInit, OnDestroy {
  @Input() selectedDocuments: any[];

  modalSubscriptions: Subscription[] = [];
  shareDocumentFormGroup: FormGroup;
  RavIconCircled = RavIconCircled;
  emailErrorMsg: string;
  faxErrorMsg: string;
  emailMessage: string;
  faxMessage: string;
  sharelinkError: string;
  shareableLink: string;
  showShareableLink = false;
  isSharingFax = false;
  isSharingEmail = false;
  shareEmailSuccessful = false;
  shareFaxSuccessful = false;
  isSharing = false;

  constructor(
    public modal: ModalService,
    private formBuilder: FormBuilder,
    private api: ApiService,
    private format: FormatService,
    private echo: EchoService
  ) {}

  ngOnInit() {
    this.shareDocumentFormGroup = this.formBuilder.group(
      {
        send_pdf_via_email: new FormControl('', [EmailValidator.validate]),
        send_pdf_via_fax: new FormControl('')
      },
      {
        validator: FormValidator.requiredAtLeastOne
      }
    );

    this.modalSubscriptions.push(
      this.modal.open.subscribe(() => {
        if (this.selectedDocuments.length === 1 && this.selectedDocuments[0].public_id) {
          this.shareableLink = `https://app.raven.com/share/${this.selectedDocuments[0].public_id}`;
          this.showShareableLink = true;
        } else {
          this.showShareableLink = false;
        }
      })
    );
    this.modalSubscriptions.push(
      this.modal.close.subscribe(() => {
        this.shareDocumentFormGroup.reset();
        this.clearMessageAndStatus();
      })
    );
  }

  ngOnDestroy(): void {
    this.modalSubscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Share the document via API request by email or fax.
   *
   * @param value The value of form group.
   */
  onClickShareButton(value: any): void {
    this.clearMessageAndStatus();
    const emailInput = value.send_pdf_via_email;
    const faxInput = value.send_pdf_via_fax;

    if (emailInput) {
      this.isSharingEmail = true;
      this.shareDocumentByEmail(emailInput);
    }
    if (faxInput) {
      this.isSharingFax = true;
      this.shareDocumentByFax(faxInput);
    }
  }

  /**
   * Make the document shareable only if the document doesn't have public ID.
   * Display the input field with shareable link.
   */
  setShareableLink(): void {
    try {
      if (!this.showShareableLink) {
        if (!this.selectedDocuments[0].public_id) {
          this.api.documents.byId.publicShare.post(this.selectedDocuments[0].id).subscribe((document) => {
            this.selectedDocuments[0].public_id = document.public_id;
            this.shareableLink = `https://app.raven.com/share/${document.public_id}`;
          });
        } else {
          this.shareableLink = `https://app.raven.com/share/${this.selectedDocuments[0].public_id}`;
        }
        this.showShareableLink = true;
      } else {
        this.api.documents.byId.publicShare.delete(this.selectedDocuments[0].id).subscribe((document) => {
          this.selectedDocuments[0].public_id = null;
          this.shareableLink = null;
          this.showShareableLink = false;
        });
      }
    } catch (error) {
      this.sharelinkError = this.getShareError(error);
    }
  }

  /***
   * Close share modal, reset form group, error message and shareable link.
   */
  cancelShare(): void {
    this.modal.close.next();
    this.shareDocumentFormGroup.reset();
    this.clearMessageAndStatus();
  }

  /**
   * Copy the link to clipboard
   */
  copyLink(): void {
    const linkBox = document.createElement('textarea');
    linkBox.style.position = 'fixed';
    linkBox.style.left = '0';
    linkBox.style.top = '0';
    linkBox.style.opacity = '0';
    linkBox.value = this.shareableLink;
    document.body.appendChild(linkBox);
    linkBox.focus();
    linkBox.select();
    document.execCommand('copy');
    document.body.removeChild(linkBox);
  }

  /**
   * Send API request and if API respond 202,  listening to a socket event for the final response
   *
   * @param input The email input
   */
  private async shareDocumentByEmail(input: string): Promise<void> {
    try {
      const apiRequests = this.selectedDocuments.map((document) => this.api.documents.byId.email
          .post(document.id, {
            email: input
          })
          .toPromise());

      await Promise.all(apiRequests);
      await this.echo.pageEmailSent();
      this.shareEmailSuccessful = true;
    } catch (error) {
      this.emailErrorMsg = this.getShareError(error);
    } finally {
      this.isSharingEmail = false;
    }
  }

  /**
   * Send API request and if API respond 202,  listening to a socket event for the final response
   *
   * @param input The fax input.
   */
  private async shareDocumentByFax(input: string): Promise<void> {
    try {
      const apiRequests = this.selectedDocuments.map((document) => this.api.documents.byId.fax
          .post(document.id, {
            to: this.format.convertPhoneNumber(input)
          })
          .toPromise());

      await Promise.all(apiRequests);
      await this.echo.documentFaxStatus();

      this.faxMessage = EchoService.faxJobQueuedMsg;
      this.shareFaxSuccessful = true;
    } catch (error) {
      const message = this.getShareError(error);

      if (message !== EchoService.faxJobQueuedMsg) {
        this.faxErrorMsg = message;
      } else {
        this.faxMessage = message;
      }
    } finally {
      this.isSharingFax = false;
    }
  }

  /**
   * Parse the error message to display on UI.
   *
   * @param error The error message.
   */
  private getShareError(error: any): any {
    if (error instanceof HttpErrorResponse) {
      return error.error.message;
    }
    return error;
  }

  /**
   * Clear all the messages and error message and reset all the send status
   */
  private clearMessageAndStatus(): void {
    this.emailErrorMsg = null;
    this.faxErrorMsg = null;
    this.emailMessage = null;
    this.faxMessage = null;
    this.shareEmailSuccessful = false;
    this.shareFaxSuccessful = false;
  }
}
